// @flow

import React from 'react';
import { getUserId, cookieJarConstants } from 'dibs-cookie-jar';

// Components
import ErrorSuccessContainer from './ErrorSuccessContainer';
import ShareModal from './ShareModal';

// Mutations
import RenderPortfolioEmails from '../mutations/RenderPortfolioEmails';
import RenderItemEmail from '../mutations/RenderItemEmail';
import RenderInvoiceEmail from '../mutations/RenderInvoiceEmail';
import RenderDesignerContactEmail from '../mutations/RenderDesignerContactEmail';
import RenderCatalogEmail from '../mutations/RenderCatalogEmail';

// Helpers
import { mutations, types } from '../utils/constants';
import { trackFavoritesEvent, trackPdpEvent } from '../utils/tracking';

// types
import type { MutationResponses } from '../utils/constants';

type ModalsProps = {
    relay: {
        environment?: {},
    },
    handleMutationResponse: (string, string) => mixed,
    catalogIds: {
        rootId: string,
        pageTypeId: string,
    },
    closeModal: () => void,
    onShareSuccess: () => void,
    onClickCopyLink: () => void,
    modalOpen?: boolean,
    mutationResponse: MutationResponses,
    personalizedMessage?: string,
    portfolioIds?: [],
    selectedItems?: [],
    invoiceAccessToken?: string,
    transactionId?: string,
    recipientEmails: ?Array<string>,
    showErrorSuccessModal?: boolean,
    senderEmail?: string,
    senderName?: string,
    title?: React$Node,
    isVerifiedTrade?: boolean,
    type: string,
};

type ModalsDefaultProps = {
    senderEmail: string,
    portfolioIds: [],
    selectedItems?: [],
    handleMutationResponse: () => void,
    onShareSuccess: () => void,
    onClickCopyLink: () => void,
    closeModal: () => void,
};

type ModalsState = {
    modalOpen: ?boolean,
    mutationResponse: MutationResponses,
    showErrorSuccessModal: ?boolean,
};

type emitMutationsArgsType = {
    shareType: string,
    message: string,
    recipientEmails: Array<string>,
    senderEmail: string,
    senderName: string,
    catalogIds?: {
        rootId: string,
        pageTypeId: string,
    },
    portfolioIds?: Array<string>,
    invoiceLink?: string,
    transactionId?: string,
    showNotes?: boolean,
    itemId: string,
    portfolioItemIds?: Array<string>, // IS THIS NECESSARY?
};

class Modals extends React.Component<ModalsProps, ModalsState> {
    static defaultProps: ModalsDefaultProps;

    constructor(props: ModalsProps) {
        super(props);

        this.state = {
            modalOpen: this.props.modalOpen,
            mutationResponse: this.props.mutationResponse,
            showErrorSuccessModal: this.props.showErrorSuccessModal,
        };

        this.emailMutations = this.emailMutations.bind(this);
    }

    componentWillReceiveProps(newProps: ModalsProps) {
        const { modalOpen } = newProps;
        this.setState({ modalOpen });
    }

    emailMutations: (args: emitMutationsArgsType) => mixed;

    emailMutations(args: emitMutationsArgsType) {
        const { relay } = this.props;
        const userId = getUserId(document.cookie, cookieJarConstants.MASQUERADE_BUYER);

        if (args.shareType === types.CATALOG) {
            RenderCatalogEmail.commit(relay.environment, {
                ...args,
                onCompleted: () => {
                    if (this.props.onShareSuccess) {
                        this.props.onShareSuccess();
                    }
                    this.onSubmitSuccess();
                },
                onError: () => {
                    this.onSubmitFailure();
                },
            });
        } else if (args.shareType === types.PDP) {
            RenderItemEmail.commit(relay.environment, {
                ...args,
                onCompleted: () => {
                    trackPdpEvent({
                        itemId: args.itemId,
                    });
                    if (this.props.onShareSuccess) {
                        this.props.onShareSuccess();
                    }
                    this.onSubmitSuccess();
                },
                onError: () => {
                    this.onSubmitFailure();
                },
            });
        } else if (args.shareType === types.INVOICE) {
            // for INVOICE, senderName is passed down from the parent; to avoid stomping on inputs in ShareModal,
            // pass them down here
            args = {
                ...args,
                senderName: this.props.senderName,
            };
            RenderInvoiceEmail.commit(relay.environment, {
                ...args,
                onCompleted: () => {
                    if (this.props.onShareSuccess) {
                        this.props.onShareSuccess();
                    }
                    this.onSubmitSuccess();
                },
                onError: () => {
                    this.onSubmitFailure();
                },
            });
        } else if (args.shareType === types.DESIGN_PROFILE) {
            RenderDesignerContactEmail.commit(relay.environment, {
                ...args,
                onCompleted: () => {
                    if (this.props.onShareSuccess) {
                        this.props.onShareSuccess();
                    }
                    this.onSubmitSuccess();
                },
                onError: () => {
                    this.onSubmitFailure();
                },
            });
        } else {
            RenderPortfolioEmails.commit(relay.environment, {
                ...args,
                userId,
                onCompleted: () => {
                    const { shareType, portfolioItemIds, showNotes } = args;
                    trackFavoritesEvent({
                        type: shareType === types.FOLDERS ? 'folder' : 'item',
                        itemCount: portfolioItemIds ? portfolioItemIds.length : 0,
                        hasNote: !!showNotes,
                    });
                    if (this.props.onShareSuccess) {
                        this.props.onShareSuccess();
                    }
                    this.onSubmitSuccess();
                    this.props.handleMutationResponse(
                        mutations.types.SHARE,
                        mutations.responses.SUCCESS
                    );
                },
                onError: () => {
                    this.onSubmitFailure();
                    this.props.handleMutationResponse(
                        mutations.types.SHARE,
                        mutations.responses.ERROR
                    );
                },
            });
        }
    }

    onModalClose() {
        const { closeModal } = this.props;
        this.setState({
            modalOpen: false,
            showErrorSuccessModal: false,
        });

        if (closeModal) {
            closeModal();
        }
    }

    onSubmitSuccess() {
        this.setState({
            showErrorSuccessModal: true,
            mutationResponse: mutations.responses.SUCCESS,
        });
    }

    onSubmitFailure() {
        this.setState({
            showErrorSuccessModal: true,
            mutationResponse: mutations.responses.ERROR,
        });
    }

    render() {
        const { selectedItems, type, invoiceAccessToken, transactionId, senderEmail } = this.props;
        const { ERROR, LOADING, NULL, SUCCESS, COMPLETE } = mutations.responses;
        const responses = [ERROR, LOADING, NULL, SUCCESS, COMPLETE];
        const selectedItemImages = selectedItems ? selectedItems.map(item => item.src) : [];
        const selectedItemTitles = selectedItems ? selectedItems.map(item => item.title) : [];

        if (
            responses.indexOf(this.state.mutationResponse) !== -1 &&
            this.state.showErrorSuccessModal
        ) {
            return (
                <ErrorSuccessContainer
                    closeModal={() => this.onModalClose()}
                    modalOpen={!!this.state.modalOpen}
                    mutationResponse={this.state.mutationResponse}
                    type={type}
                />
            );
        } else {
            return (
                <ShareModal
                    catalogIds={this.props.catalogIds}
                    closeModal={() => this.onModalClose()}
                    modalOpen={!!this.state.modalOpen}
                    onShare={(...args) => this.emailMutations(...args)}
                    selectedItems={selectedItems}
                    selectedItemImages={selectedItemImages.length ? selectedItemImages : []}
                    selectedItemTitles={selectedItemTitles}
                    invoiceAccessToken={invoiceAccessToken}
                    isVerifiedTrade={this.props.isVerifiedTrade}
                    recipientEmails={this.props.recipientEmails}
                    transactionId={transactionId}
                    title={this.props.title}
                    type={type}
                    senderEmail={senderEmail}
                    onClickCopyLink={this.props.onClickCopyLink || (() => {})}
                    personalizedMessage={this.props.personalizedMessage || ''}
                    portfolioIds={this.props.portfolioIds}
                />
            );
        }
    }
}

export default Modals;
